import { useState, FormEvent } from 'react';
import { t, useTranslation } from '../i18n';

const COMMON_BAD = new Set([
  '12345678', '123456789', '1234567890', 'abcdefgh', 'qwertyui', 'qwerty12',
  'password', 'password1', 'passw0rd', '11111111', '00000000', 'aaaaaaaa',
  'iloveyou', 'admin1234', 'syphrax', 'frecuencia', 'general', 'letmein1',
]);

function patternRisk(s: string): string | null {
  const lower = s.toLowerCase();
  if (COMMON_BAD.has(lower)) return t('modal.freq.errCommon');
  if (/^(.)\1+$/.test(lower)) return t('modal.freq.errRepeat');
  if (/^(0?123|1234|2345|3456|4567|5678|6789|7890)/.test(lower)) return t('modal.freq.errSeqNum');
  if (/^(abcd|bcde|cdef|defg)/.test(lower)) return t('modal.freq.errSeqAlpha');
  return null;
}

interface Props {
  onCancel: () => void;
  onActivate: (name: string) => void | Promise<void>;
}

export default function FrequencyModal({ onCancel, onActivate }: Props) {
  useTranslation();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const cleaned = value.trim();
    if (cleaned.length < 8) {
      setError(t('modal.freq.errShort'));
      return;
    }
    const weak = patternRisk(cleaned);
    if (weak) {
      setError(weak);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onActivate(cleaned);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t('modal.freq.title')}</h2>
        <p className="hint">{t('modal.freq.hint')}</p>
        <form onSubmit={submit}>
          <input
            type="text"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('modal.freq.placeholder')}
            disabled={busy}
            minLength={8}
            autoCapitalize="none"
            autoCorrect="off"
          />
          <div className="modal-actions">
            <button type="button" className="ghost" onClick={onCancel} disabled={busy}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? t('modal.freq.deriving') : t('modal.freq.activate')}
            </button>
          </div>
          {error && <div className="error">{error}</div>}
          <p className="warn">{t('modal.freq.warn')}</p>
        </form>
      </div>
    </div>
  );
}
